import { addSeconds } from 'date-fns';
import { action, observable } from 'mobx';
import { autostore, HttpClient, IConfig } from 'Mri.Web.Common';

export class DisplaysChartsStore {
    public constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;

        autostore(this.storage, 'DisplaysChartsStore.DisplaysChartsStorage');
    }

    @action
    public async loadChartConfigAsync(serialNumber: string) {
        const chartConfig = await this.httpClient.get<Mri.DisplayCharts.ChartConfigPoll>(
            `/api/v1/displays/${serialNumber}/charts`
        );

        if (chartConfig) this.displaysCharts.set(chartConfig.displaySerialNumber, chartConfig);
    }

    @action
    public async loadTrackDataAsync(
        serialNumber: string,
        varNum: number,
        startTime: Date,
        endTime: Date,
        samplePeriodSeconds: number,
        append: boolean,
        config?: IConfig
    ) {
        if (!this.displayTrackDatas.has(serialNumber)) {
            this.displayTrackDatas.set(serialNumber, new Map<number, Mri.DisplayCharts.TrackDataPoll>());
        }

        const trackDatas = this.displayTrackDatas.get(serialNumber)!;
        const existingTrackDataPoll = trackDatas.get(varNum);

        if (append && existingTrackDataPoll) {
            const { trackData, trackStartTime, secsPerSample } = existingTrackDataPoll;
            const existingDataLength = trackData.length * secsPerSample;

            startTime = addSeconds(trackStartTime, existingDataLength + secsPerSample);

            if (startTime >= endTime) return;
        }

        config = {
            ...config,

            params: {
                startTime,
                endTime,
                samplePeriodSeconds,
            },
        };

        const trackDataPoll = await this.httpClient.get<Mri.DisplayCharts.TrackDataPoll>(
            `/api/v1/displays/${serialNumber}/tracks/${varNum}`,
            config
        );

        if (trackDataPoll) {
            if (append && existingTrackDataPoll) {
                for (const v of trackDataPoll.trackData) {
                    existingTrackDataPoll.trackData.push(v);
                }
            } else {
                trackDatas.set(varNum, trackDataPoll);
            }
        }
    }

    @action
    public async loadTelemetryInfoAsync(serialNumber: string) {
        const info = await this.httpClient.get<Mri.TelemetryPolls.TelemetryInfo>(
            `/api/v1/displays/${serialNumber}/telemetry/info`
        );

        if (info) this.displaysTelemetryInfos.set(serialNumber, info);
    }

    @action
    public async loadTelemetryMeasurementsAsync(
        serialNumber: string,
        measurementId: Mri.WebApi.MeasurementId,
        startTime: Date,
        endTime: Date
    ) {
        if (!this.displayMeasurementDatas.has(serialNumber)) {
            this.displayMeasurementDatas.set(serialNumber, new Map<number, Mri.TelemetryPolls.MeasurementPoint[]>());
        }

        const measurementDatas = this.displayMeasurementDatas.get(serialNumber)!;

        const limit = 800;

        const measurementData = await this.httpClient.get<Mri.TelemetryPolls.MeasurementPoint[]>(
            `/api/v1/displays/${serialNumber}/telemetry/measurement/${measurementId}`,
            {
                params: {
                    startTime,
                    endTime,
                    limit,
                },
            }
        );

        if (measurementData) measurementDatas.set(measurementId, measurementData);
    }

    @observable
    public displaysCharts = new Map<string, Mri.DisplayCharts.ChartConfigPoll>();
    @observable
    public displayTrackDatas = new Map<string, Map<number, Mri.DisplayCharts.TrackDataPoll>>();
    @observable
    public displaysTelemetryInfos = new Map<string, Mri.TelemetryPolls.TelemetryInfo>();
    @observable
    public displayMeasurementDatas = new Map<string, Map<number, Mri.TelemetryPolls.MeasurementPoint[]>>();

    @observable
    public storage = new DisplaysChartsStorage();

    private readonly httpClient: HttpClient;
}

class DisplaysChartsStorage {
    public static version = 1;

    public isValid() {
        return this.version == DisplaysChartsStorage.version;
    }

    @observable
    public version = DisplaysChartsStorage.version;
    @observable
    public telemetryChartSelectedMeasurements: Mri.WebApi.MeasurementId[] = [];
    @observable
    public telemetryChartStartTime: Date = new Date();
    @observable
    public telemetryChartEndTime: Date = new Date();
}
